import axios, { AxiosResponse } from 'axios';
import { CalibrationFeedProgram } from 'views/bluviu-nutrition/features/calibrations/types/calibration-feed-program.type';
import { CloneCalibrationRequest } from 'views/bluviu-nutrition/features/calibrations/types/clone-calibration-request.type';
import { CreateCalibrationRequest } from 'views/bluviu-nutrition/features/calibrations/types/create-calibration-request.type';
import { SimpleCalibration } from 'views/bluviu-nutrition/features/calibrations/types/simple-calibration.type';
import { UpdateCalibrationFeedProgramRequest } from 'views/bluviu-nutrition/features/calibrations/types/update-calibration-feed-program-request.type';
import { UpdateSimpleCalibrationInputsRequest } from 'views/bluviu-nutrition/features/calibrations/types/update-simple-calibration-inputs-request.type';

/**
 * Get a simple calibration
 * @param globalSiteId
 * @param calibrationId
 * @returns
 */
export const getSimpleCalibration = async (globalSiteId: string, calibrationId: string): Promise<SimpleCalibration> => {
  //   return CalibrationData;
  const res = await axios.get<SimpleCalibration>(
    `/ewosapi/v1/calibration/gt2/simple?globalSiteId=${globalSiteId}&calibrationId=${calibrationId}`
  );

  return res.data;
};

/**
 * Create a simple calibration
 * @param globalSiteId
 * @param request
 * @returns
 */
export const postSimpleCalibration = async (globalSiteId: string, request: CreateCalibrationRequest): Promise<SimpleCalibration> => {
  //   return CalibrationData;
  const res = await axios.post(`/ewosapi/v1/calibration/gt2/simple?globalSiteId=${globalSiteId}`, request);

  return res.data; // should value be removed? discuss with BE
};

export const cloneSimpleCalibration = async (globalSiteId: string, request: CloneCalibrationRequest): Promise<SimpleCalibration> => {
  //   return CalibrationData;
  const res = await axios.post(`/ewosapi/v1/calibration/gt2/simple/clone?globalSiteId=${globalSiteId}`, request);
  return res.data;
};

/**
 * Update the calibration settings for the Site with the specified globalSiteId.
 * @param globalSiteId The site to be calibrated
 * @param request `UpdateCalibrationSettingsRequest`
 * @returns `Calibration`
 */
export const putSimpleCalibrationInputs = async (
  globalSiteId: string,
  calibrationId: string,
  request: UpdateSimpleCalibrationInputsRequest
): Promise<SimpleCalibration> => {
  const res = await axios.put(
    `/ewosapi/v1/calibration/gt2/simple?globalSiteId=${globalSiteId}&calibrationId=${calibrationId}`,
    request
  );
  return res.data;
};

/**
 * Get the Feed Program for a simple calibration
 * @param globalSiteId
 * @param calibrationId
 * @returns `SimpleCalibrationFeedProgram[]`
 */
export const getSimpleCalibrationFeedProgram = async (
  globalSiteId: string,
  calibrationId: string
): Promise<CalibrationFeedProgram[]> => {
  const res = await axios.get<CalibrationFeedProgram[]>(
    `/ewosapi/v1/calibration/gt2/simple/feed-program?globalSiteId=${globalSiteId}&calibrationId=${calibrationId}`
  );

  return res.data;
};

/**
 * Update a site's calibration feed program
 * @param globalSiteId The site to be calibrated
 * @param request `UpdateCalibrationSettingsRequest`
 * @returns `Calibration`
 */
export const putSimpleCalibrationFeedProgram = async (
  globalSiteId: string,
  calibrationId: string,
  request: UpdateCalibrationFeedProgramRequest[]
): Promise<AxiosResponse> => {
  const res = await axios.put(
    `/ewosapi/v1/calibration/gt2/simple/feed-program?globalSiteId=${globalSiteId}&calibrationId=${calibrationId}`,
    request
  );
  return res.data;
};

/**
 *
 * Gets a calibration input file. Requires being a Super User
 * @param calibrationId
 * @returns
 */
export const getSimpleCalibrationInputFile = async (calibrationId: string): Promise<AxiosResponse> => {
  return await axios.get<AxiosResponse>(`/ewosapi/v1/calibration/gt2/simple/file/input?calibrationId=${calibrationId}`, {
    responseType: 'blob',
  });
};

/**
 *
 * Gets the calibration output file. Requires being a Super User
 * /ewosapi/v1/calibration/gt2/file/output?globalSiteId={globalSiteId}
 * @param calibrationId
 */
export const getSimpleCalibrationOutputFile = async (calibrationId: string): Promise<AxiosResponse> => {
  return await axios.get<AxiosResponse>(`/ewosapi/v1/calibration/gt2/simple/file/output?calibrationId=${calibrationId}`, {
    responseType: 'blob',
  });
};

/**
 *
 * Trigger a simple cage calibration for a site. This begins the process of a calibration and can be monitored with the `getSimpleCalibrationStatus` api call.
 * @param globalSiteId
 * @param calibrationId
 * @returns
 */
export const runSimpleCalibration = async (globalSiteId: string, calibrationId: string): Promise<AxiosResponse> => {
  return await axios.put(`/ewosapi/v1/calibration/gt2/simple/calibrate?globalSiteId=${globalSiteId}&calibrationId=${calibrationId}`);
};

/**
 * Get the status of a simple calibration for a given site.
 * @param globalSiteId
 * @param calibrationId
 * @returns
 */
export const getSimpleCalibrationStatus = async (
  globalSiteId: string,
  calibrationId: string
): Promise<{ calibrationIsRunning: boolean }> => {
  const res = await axios.get<{ calibrationIsRunning: boolean }>(
    `/ewosapi/v1/calibration/gt2/simple/status?globalSiteId=${globalSiteId}&calibrationId=${calibrationId}`
  );
  return res.data;
};
